// What happens to the key on this device when the tenant key is rotated.
//
// Split from `vault-unlock.ts` on size, but the seam is a real one:
// everything here runs AFTER `rotateTenantKey` has committed
// server-side, which changes the rules. At that point the caller is
// holding the only copy of the new private key and is about to render it
// as a recovery file, so a throw from this module does not fail a
// rotation — it loses the file. That is why both helpers below swallow
// into a `console.warn` rather than propagating, which would be the
// wrong answer anywhere else in this subsystem and is spelled out on
// each of them.
//
// Re-exported through `vault-unlock.ts`, which is the path every call
// site imports from.
import { unb64u } from '@stoaria/vault-core'
import { forget, hasStoredKey, store } from './vault-key-store.ts'
import { adoptImportedKey, getSession } from './vault-key-session.ts'

/**
 * Replaces the held key without re-verifying it against a server-reported
 * public key — the one deliberate exception to `unlock()`'s mismatch
 * check. Used only by `VaultTenantKey`'s rotate flow: the caller
 * generated the new keypair itself and `rotateTenantKey` has already
 * succeeded, so it holds stronger proof than a pasted recovery file ever
 * could. Not a general-purpose setter — nothing else should call this.
 *
 * Takes the new public half too, which `unlock()` gets from the server: a
 * non-extractable handle cannot produce it, and `open()` needs it as an
 * HKDF salt input on every later read.
 *
 * Does NOT zero the caller's array. The rotate flow still has to render
 * that key as the new recovery file, and wiping it here would hand the
 * operator a file full of zeroes — the single worst outcome this module
 * could produce. It copies, and zeroes the copy.
 *
 * Whether the new key is kept on this device is not a parameter: it is
 * whatever the answer was for the key being replaced. A rotation is not
 * the moment to re-ask a question the operator answered at unlock, and
 * silently arming (or disarming) a browser behind a Rotate button would
 * be a surprise either way.
 */
export async function replacePrivateKey(
  newPrivateKey: Uint8Array,
  newTenantPubkey: string,
): Promise<void> {
  const pkcs8 = new Uint8Array(newPrivateKey)
  const previous = getSession()
  const rootId = previous?.rootId ?? null
  try {
    // Asked BEFORE the adopt, because the answer is about the outgoing
    // key: once the session points at the new public key, the old
    // record — which is keyed by the old one — is no longer findable.
    const wasStored =
      rootId !== null &&
      previous !== null &&
      (await wasKeyStored(rootId, previous.tenantPubkey))
    const key = await adoptImportedKey(
      pkcs8,
      newTenantPubkey,
      unb64u(newTenantPubkey),
      rootId,
    )
    if (rootId !== null)
      await rePersist(rootId, newTenantPubkey, key, wasStored)
  } finally {
    pkcs8.fill(0)
  }
}

/** "Did this device have the outgoing key?", answered without ever
 * failing the rotation around it.
 *
 * `hasStoredKey` is contractually quiet (false, never a throw, where
 * there is no IndexedDB), so this guard should never fire. It exists
 * because of WHERE it is called: inside `replacePrivateKey`, after
 * `rotateTenantKey` has already committed server-side and before the
 * caller renders the only copy of the new recovery file. A throw there
 * loses that file permanently. Reported, never silent, and the
 * conservative answer is `false` — not re-storing costs one paste, while
 * guessing `true` and failing the write costs the same paste plus a
 * stale record left on the disk. */
async function wasKeyStored(
  rootId: string,
  tenantPubkey: string,
): Promise<boolean> {
  try {
    return await hasStoredKey(rootId, tenantPubkey)
  } catch (err) {
    console.warn(
      'could not tell whether this device had the outgoing tenant key; ' +
        'treating it as not stored',
      err,
    )
    return false
  }
}

/** Retires the pre-rotation record and, if this device had one, writes
 * the new key in its place.
 *
 * The `forget` is not optional housekeeping, and it runs even when
 * nothing is written back. The old record still holds the OLD private
 * key, which after a rotation is exactly the material an attacker wants
 * and the operator believes is retired. It is keyed by (root, public
 * key) so it can never be mistaken for the new one, but "cannot be
 * confused with" is not "is not on the disk".
 *
 * Never throws, and that is deliberate: by the time this runs the
 * rotation is already committed server-side and the caller is about to
 * show the only copy of the new recovery file. Failing the rotate over a
 * storage error would lose that file. Losing the stored copy instead
 * costs one paste on the next reload, and the console line says so.
 */
async function rePersist(
  rootId: string,
  tenantPubkey: string,
  key: CryptoKey,
  wasStored: boolean,
): Promise<void> {
  try {
    await forget(rootId)
    if (wasStored) await store({ rootId, tenantPubkey, key })
  } catch (err) {
    console.warn(
      'tenant key rotated but could not be re-stored on this device; ' +
        'the next unlock will ask for the recovery file',
      err,
    )
  }
}
